import { ArrowLeft } from "lucide-react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import type { MonitoringRelease } from "../data/monitoring";

export function MonitoringAppendix({ release }: { release: MonitoringRelease }) {
  const base = `/reports/${release.manifest.release_id}`;
  const heroStyle = release.manifest.hero_background_url
    ? { "--hero-image": `url(${import.meta.env.BASE_URL}${release.manifest.hero_background_url})` } as CSSProperties
    : undefined;
  return <article id="method-appendix" className="monitoring-appendix-page">
    <header className="monitoring-appendix-page__hero" style={heroStyle}>
      <Link to={base}><ArrowLeft size={16} aria-hidden="true" />Back to Report</Link>
      <span>Appendix</span>
      <h1>Methodology & Definitions</h1>
      <p>Reference material for the scoring, comparisons, and client-safe interpretations used throughout this conversation and sentiment report.</p>
    </header>

    <section className="monitoring-appendix-page__section">
      <span className="visual-kicker">Report Foundation</span>
      <h2>Source Windows & Theme Assignment</h2>
      <div className="method-detail-grid">
        <article><h3>Reporting Windows</h3><p>Current reporting window: <b>{release.manifest.current_window}</b>. {release.manifest.variant === "comparison" ? `Baseline comparison window: ${release.manifest.baseline_window}.` : "This edition focuses on the current reporting window and does not present prior-window comparisons."}</p></article>
        <article><h3>Theme Taxonomy</h3><p>Each message turn is assigned against the saved analyst-reviewed theme taxonomy. Reviewed emerging themes are retained as New rather than being treated as historical movers.</p></article>
        <article><h3>Conversation Measures</h3><p>Theme Share is the proportion of classified message turns assigned to a theme. Negativity Rate is the share of that theme’s classified turns scored negative.</p></article>
      </div>
    </section>

    <section className="monitoring-appendix-page__section">
      <span className="visual-kicker">Scoring Algorithms</span>
      <h2>How Core Scores Are Derived</h2>
      <div className="method-formula-grid">
        <article><span>Priority Index</span><strong>100 × Theme Share × Risk Mix</strong><p>Risk Mix weights negative rate at 60%, observed toxicity at 25%, and lack of positivity at 15%. Neutral conversation is not treated as risk.</p></article>
        <article><span>Cohort Index</span><strong>Selected Subgroup ÷ All Others</strong><p>Theme Share and Negativity Rate are separately indexed. A value of 1.00× indicates parity with the counterpart; values above or below it show relative over- or under-indexing.</p></article>
        <article><span>Connection Lift</span><strong>Observed Shared Audience ÷ Expected Overlap</strong><p>Links require at least 25 shared users and 1.25× lift. They show same-user, same-wave co-discussion—not causal propagation.</p></article>
      </div>
    </section>

    <section className="monitoring-appendix-page__section">
      <span className="visual-kicker">Connected Conversation</span>
      <h2>Audience De-duplication & Portfolio Selection</h2>
      <div className="method-detail-grid method-detail-grid--two">
        <article><h3>Shared-Audience Map</h3><p>A theme pair is connected when the same anonymized user discusses both themes in the same reporting window more often than expected from their standalone discussion prevalence. No user identifiers are exported or displayed.</p></article>
        <article><h3>Three-Theme Portfolio</h3><p>The first theme is the highest current Priority Index. Each next selection discounts the portion of its discussion audience already covered by earlier selections, so the recommendation prioritizes distinct conversation coverage rather than overlapping audiences.</p></article>
      </div>
    </section>

    <section className="monitoring-appendix-page__section">
      <span className="visual-kicker">Interpretation Boundaries</span>
      <h2>How to Read the Findings</h2>
      <ul className="method-limitations">
        <li>Metrics describe the observed conversation in the supplied community data; they do not estimate population prevalence or establish causality.</li>
        <li>Shared-audience connections identify co-discussion and connected concerns. They do not mean that addressing one theme will cause another to change.</li>
        <li>Priority movement is available only for themes with a comparable prior-window record. Newly introduced themes receive a current score but no historical change.</li>
        {release.method_notes.map((note) => <li key={note}>{note}</li>)}
      </ul>
    </section>
  </article>;
}
